import type {
  FieldDefinition,
  ScalarFieldValueType,
  TableScalarValue,
  TableSourceWarning,
} from '../model';

export type TabularImportFormat = 'csv' | 'xlsx' | 'gpkg';
export type TabularRawCell = boolean | Date | number | string | null | undefined;

export type TabularImportErrorCode =
  | 'empty-table'
  | 'file-too-large'
  | 'invalid-csv'
  | 'invalid-xlsx'
  | 'no-header'
  | 'no-usable-worksheet'
  | 'too-many-cells'
  | 'too-many-fields'
  | 'too-many-rows'
  | 'unsupported-encoding';

export interface TabularImportError {
  readonly code: TabularImportErrorCode;
  readonly message: string;
}

export interface ImportedTableData {
  readonly fields: readonly FieldDefinition[];
  readonly rows: readonly (readonly TableScalarValue[])[];
  readonly suggestedTableName: string;
  readonly provenance: {
    readonly originalFileName: string;
    readonly originalFormat: TabularImportFormat;
    readonly worksheetName?: string;
  };
  readonly warnings: readonly TableSourceWarning[];
}

export interface TabularSafetyLimits {
  readonly maximumCells: number;
  readonly maximumFields: number;
  readonly maximumRows: number;
}

export type NormalizeTabularResult =
  | { readonly ok: true; readonly value: ImportedTableData }
  | { readonly ok: false; readonly error: TabularImportError };

export const TABULAR_INFERENCE_SAMPLE_SIZE = 1_000;

const STRICT_NUMBER = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/;
const SIGNIFICANT_LEADING_ZERO = /^[+-]?0\d/;
const ISO_DATE = /^\d{4}-(\d{2})-(\d{2})$/;
const ISO_DATE_TIME =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,9})?)?(?:Z|[+-]\d{2}:?\d{2})$/;

function failure(code: TabularImportErrorCode, message: string): NormalizeTabularResult {
  return { ok: false, error: { code, message } };
}

function blank(value: TabularRawCell): boolean {
  return (
    value === null || value === undefined || (typeof value === 'string' && value.trim() === '')
  );
}

function rowIsBlank(row: readonly TabularRawCell[]): boolean {
  return row.every(blank);
}

function validIsoDate(value: string): boolean {
  const dateOnly = ISO_DATE.exec(value);
  if (dateOnly) {
    const date = new Date(`${value}T00:00:00.000Z`);
    return (
      !Number.isNaN(date.valueOf()) &&
      date.getUTCMonth() + 1 === Number(dateOnly[1]) &&
      date.getUTCDate() === Number(dateOnly[2])
    );
  }
  return ISO_DATE_TIME.test(value) && !Number.isNaN(Date.parse(value));
}

function numericText(value: string): boolean {
  return (
    STRICT_NUMBER.test(value.trim()) &&
    !SIGNIFICANT_LEADING_ZERO.test(value.trim()) &&
    Number.isFinite(Number(value))
  );
}

function csvCandidateType(values: readonly TabularRawCell[]): ScalarFieldValueType {
  const text = values as readonly string[];
  if (text.every((value) => /^(?:true|false)$/i.test(value.trim()))) return 'boolean';
  if (text.every(numericText)) {
    return text.every((value) => Number.isInteger(Number(value))) ? 'integer' : 'number';
  }
  if (text.every((value) => validIsoDate(value.trim()))) return 'date';
  return 'string';
}

function xlsxCandidateType(values: readonly TabularRawCell[]): ScalarFieldValueType {
  if (values.every((value) => typeof value === 'boolean')) return 'boolean';
  if (values.every((value) => typeof value === 'number' && Number.isFinite(value))) {
    return values.every((value) => Number.isInteger(value)) ? 'integer' : 'number';
  }
  if (values.every((value) => value instanceof Date && !Number.isNaN(value.valueOf()))) {
    return 'date';
  }
  return 'string';
}

function valueMatchesType(value: TabularRawCell, type: ScalarFieldValueType): boolean {
  if (blank(value)) return true;
  if (type === 'string') return true;
  if (type === 'boolean') {
    return (
      typeof value === 'boolean' ||
      (typeof value === 'string' && /^(?:true|false)$/i.test(value.trim()))
    );
  }
  if (type === 'integer' || type === 'number') {
    if (typeof value === 'number') {
      return Number.isFinite(value) && (type === 'number' || Number.isInteger(value));
    }
    return (
      typeof value === 'string' &&
      numericText(value) &&
      (type === 'number' || Number.isInteger(Number(value)))
    );
  }
  return value instanceof Date
    ? !Number.isNaN(value.valueOf())
    : typeof value === 'string' && validIsoDate(value.trim());
}

function dateScalar(value: Date): string {
  const iso = value.toISOString();
  return iso.endsWith('T00:00:00.000Z') ? iso.slice(0, 10) : iso;
}

function stringScalar(value: Exclude<TabularRawCell, null | undefined>): string {
  if (value instanceof Date) return dateScalar(value);
  return String(value);
}

function convertValue(value: TabularRawCell, type: ScalarFieldValueType): TableScalarValue {
  if (blank(value)) return null;
  if (type === 'string') {
    return stringScalar(value as Exclude<TabularRawCell, null | undefined>);
  }
  if (type === 'date') {
    return value instanceof Date ? dateScalar(value) : String(value).trim();
  }
  if (type === 'boolean') {
    return typeof value === 'boolean' ? value : String(value).trim().toLowerCase() === 'true';
  }
  return typeof value === 'number' ? value : Number(String(value).trim());
}

function uniqueHeaders(values: readonly TabularRawCell[]): readonly string[] {
  const used = new Set<string>();
  const nextSuffix = new Map<string, number>();
  return values.map((value, index) => {
    const base = blank(value)
      ? `Column ${index + 1}`
      : stringScalar(value as Exclude<TabularRawCell, null | undefined>).trim();
    let candidate = base;
    let suffix = nextSuffix.get(base) ?? 2;
    while (used.has(candidate)) {
      candidate = `${base} (${suffix})`;
      suffix += 1;
    }
    nextSuffix.set(base, suffix);
    used.add(candidate);
    return candidate;
  });
}

function inferredType(
  values: readonly TabularRawCell[],
  format: TabularImportFormat,
): ScalarFieldValueType {
  const meaningful = values.filter((value) => !blank(value));
  if (meaningful.length === 0) return 'string';
  const sample = meaningful.slice(0, TABULAR_INFERENCE_SAMPLE_SIZE);
  const candidate = format === 'csv' ? csvCandidateType(sample) : xlsxCandidateType(sample);
  return meaningful.every((value) => valueMatchesType(value, candidate)) ? candidate : 'string';
}

export function inferCsvColumnType(values: readonly string[]): ScalarFieldValueType {
  return inferredType(values, 'csv');
}

export function normalizeTabularRows(
  rawRows: readonly (readonly TabularRawCell[])[],
  options: {
    readonly fileName: string;
    readonly format: TabularImportFormat;
    readonly limits: TabularSafetyLimits;
    readonly suggestedTableName: string;
    readonly warnings?: readonly TableSourceWarning[];
    readonly worksheetName?: string;
  },
): NormalizeTabularResult {
  const formatLabel = options.format.toUpperCase();
  const meaningfulRows = rawRows.filter((row) => !rowIsBlank(row));
  const headerRow = meaningfulRows[0];
  if (!headerRow) return failure('no-header', `The ${formatLabel} file needs a usable header row.`);

  const dataRows = meaningfulRows.slice(1);
  let fieldCount = headerRow.length;
  while (
    fieldCount > 0 &&
    blank(headerRow[fieldCount - 1]) &&
    dataRows.every((row) => blank(row[fieldCount - 1]))
  ) {
    fieldCount -= 1;
  }
  if (fieldCount === 0)
    return failure('no-header', `The ${formatLabel} file needs a usable header row.`);
  if (fieldCount > options.limits.maximumFields) {
    return failure(
      'too-many-fields',
      `The ${formatLabel} data has more than ${options.limits.maximumFields.toLocaleString()} columns.`,
    );
  }
  if (dataRows.length === 0) {
    return failure('empty-table', `The ${formatLabel} file contains a header but no data rows.`);
  }
  if (dataRows.length > options.limits.maximumRows) {
    return failure(
      'too-many-rows',
      `The ${formatLabel} data has more than ${options.limits.maximumRows.toLocaleString()} rows.`,
    );
  }
  if (fieldCount * dataRows.length > options.limits.maximumCells) {
    return failure(
      'too-many-cells',
      `The ${formatLabel} data is too large and wide to import safely in this browser.`,
    );
  }

  const overflowIndex = dataRows.findIndex((row) =>
    row.slice(fieldCount).some((value) => !blank(value)),
  );
  if (overflowIndex >= 0) {
    return failure(
      options.format === 'csv' ? 'invalid-csv' : 'invalid-xlsx',
      `${formatLabel} row ${overflowIndex + 2} contains populated cells beyond the header columns.`,
    );
  }

  const headers = uniqueHeaders(headerRow.slice(0, fieldCount));
  let missingCellRows = 0;
  const normalizedRawRows = dataRows.map((row) => {
    if (row.length < fieldCount) missingCellRows += 1;
    return Array.from({ length: fieldCount }, (_, column) => row[column] ?? null);
  });

  const fields = headers.map((name, column): FieldDefinition => {
    const values = normalizedRawRows.map((row) => row[column]);
    const nullCount = values.filter(blank).length;
    return {
      name,
      type: inferredType(values, options.format),
      nullable: nullCount > 0,
      presentCount: values.length - nullCount,
      nullCount,
    };
  });
  const rows = normalizedRawRows.map((row) =>
    row.map((value, column) => convertValue(value, fields[column]?.type as ScalarFieldValueType)),
  );
  const warnings = [...(options.warnings ?? [])];
  if (missingCellRows > 0) {
    warnings.push({
      code: 'missing-cells',
      message: `${missingCellRows.toLocaleString()} ${missingCellRows === 1 ? 'row was' : 'rows were'} shorter than the header and missing cells were stored as null.`,
    });
  }

  return {
    ok: true,
    value: {
      fields,
      rows,
      suggestedTableName: options.suggestedTableName,
      provenance: {
        originalFileName: options.fileName,
        originalFormat: options.format,
        ...(options.worksheetName ? { worksheetName: options.worksheetName } : {}),
      },
      warnings,
    },
  };
}

export function representativeFieldValue(
  table: ImportedTableData,
  fieldName: string,
): TableScalarValue | null {
  const column = table.fields.findIndex((field) => field.name === fieldName);
  if (column < 0) return null;
  const limit = Math.min(table.rows.length, TABULAR_INFERENCE_SAMPLE_SIZE);
  for (let index = 0; index < limit; index += 1) {
    const value = table.rows[index]?.[column];
    if (value !== null && value !== undefined && value !== '') return value;
  }
  return null;
}
